const ISSUE_STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'before', 'by', 'for', 'from', 'how',
  'if', 'in', 'into', 'is', 'it', 'of', 'on', 'or', 'that', 'the', 'their', 'there',
  'this', 'to', 'under', 'what', 'when', 'where', 'whether', 'which', 'with',
]);

const AUTHORITY_BUCKET_META = {
  binding: {
    label: 'Binding',
    helper: 'Primary authorities to anchor submissions and drafting.',
  },
  statutory: {
    label: 'Statutory / Government',
    helper: 'Bare Act, rules, circulars, notifications, and official material.',
  },
  strong: {
    label: 'Strong support',
    helper: 'Highly useful authorities that strengthen the line of argument.',
  },
  persuasive: {
    label: 'Persuasive',
    helper: 'Helpful but forum-dependent or fact-sensitive support.',
  },
  background: {
    label: 'Background',
    helper: 'Contextual material that should not carry the core proposition alone.',
  },
};

const WORKFLOW_MODE_DETAILS = {
  MEMO: {
    title: 'Research Desk Note',
    summary: 'Turn the issue into a usable advice note with authority, risk, and next steps.',
    outputLabel: 'Advice-ready note',
  },
  HEARING: {
    title: 'Hearing Note Mode',
    summary: 'Compress the matter into oral points, bench pressure, fallback asks, and binding support.',
    outputLabel: 'Courtroom speaking sheet',
  },
  BRIEF: {
    title: 'Case Brief Mode',
    summary: 'Digest one judgment into holdings, ratio, later treatment, and practical relevance.',
    outputLabel: 'Portable case digest',
  },
  PETITION: {
    title: 'Petition Grounds Mode',
    summary: 'Extract legally supportable grounds, weak spots, and relief framing before drafting.',
    outputLabel: 'Grounds-first draft base',
  },
  BENCH: {
    title: 'Bench Question Mode',
    summary: 'Pressure-test the issue through likely judicial questions and crisp answers.',
    outputLabel: 'Bench question rehearsal',
  },
  IRAC: {
    title: 'IRAC Mode',
    summary: 'Structure the problem into issue, rule, application, and conclusion with discipline.',
    outputLabel: 'Structured analysis',
  },
};

function normalizeText(value) {
  return String(value || '').trim();
}

function normalizeLower(value) {
  return normalizeText(value).toLowerCase();
}

function normalizeList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function normalizeResearchIntelligence(raw = {}) {
  const value = raw && typeof raw === 'object' ? raw : {};
  return {
    propositionLedger: normalizeList(value.proposition_ledger || value.propositionLedger),
    treatmentSignals: normalizeList(value.treatment_signals || value.treatmentSignals),
    adverseAuthorities: normalizeList(value.adverse_authorities || value.adverseAuthorities),
    proofGaps: normalizeList(value.proof_gaps || value.proofGaps),
    reliefPosture: normalizeList(value.relief_posture || value.reliefPosture),
    jurisdictionMap: normalizeList(value.jurisdiction_map || value.jurisdictionMap),
    readiness: value.readiness && typeof value.readiness === 'object' ? value.readiness : {},
    queryClass: normalizeText(value.query_class || value.queryClass),
  };
}

function dedupeList(items) {
  const seen = new Set();
  return items.filter((item) => {
    const key = normalizeLower(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function tokenizeIssue(text) {
  return normalizeLower(text)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((token) => token.length > 2 && !ISSUE_STOP_WORDS.has(token));
}

function dedupeSourcesById(sources) {
  const seen = new Set();
  return (sources || []).filter((source) => {
    const key = source?.id || source?.tid || source?.url || source?.title;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getSourceText(source) {
  return [
    source?.title,
    source?.reason,
    source?.headline,
    source?.authority_strength_label,
    source?.trust_label,
    source?.docsource,
    ...(Array.isArray(source?.headnotes) ? source.headnotes : []),
    ...(Array.isArray(source?.section_headings) ? source.section_headings : []),
  ].filter(Boolean).join(' ');
}

export function getAuthorityCourt(source) {
  const text = normalizeLower(`${source?.docsource || ''} ${source?.url || ''}`);
  if (text.includes('supreme court')) return 'SC';
  if (text.includes('high court')) return 'HC';
  if (text.includes('tribunal') || text.includes('commission')) return 'TRIBUNAL';
  if (
    text.includes('.gov.in')
    || normalizeLower(source?.source_family).includes('government')
    || normalizeLower(source?.trust_label).includes('government')
  ) {
    return 'GOV';
  }
  return 'OTHER';
}

export function inferAuthorityTier(source, primaryCourt = 'GENERAL') {
  const strength = normalizeLower(source?.authority_strength_label);
  const trust = normalizeLower(source?.trust_label);
  const court = getAuthorityCourt(source);

  if (trust.includes('government') || court === 'GOV') return 'statutory';
  if (strength.includes('binding')) return 'binding';
  if (court === 'SC') return 'binding';
  if (court === 'HC' && primaryCourt === 'HC') return 'binding';
  if (court === 'HC') return 'strong';
  if (strength.includes('persuasive') || court === 'TRIBUNAL') return 'persuasive';
  return 'background';
}

export function inferIssueList(researchTopic = '', decomposedIssues = []) {
  if (Array.isArray(decomposedIssues) && decomposedIssues.length > 0) {
    return dedupeList(decomposedIssues.map((issue) => normalizeText(issue)).filter(Boolean));
  }

  const topic = normalizeText(researchTopic);
  if (!topic) return [];

  const rawPieces = topic
    .split(/\n|;|\?|\.(?=\s+[A-Z])|,(?=\s*(?:where|whether|after|before|despite|because))/i)
    .flatMap((piece) => piece.split(/\s+\band\b\s+(?=(?:the|a|an|maintainability|enforcement|specific|service|section|writ))/i))
    .map((piece) => normalizeText(piece))
    .filter(Boolean);

  return dedupeList(rawPieces.length > 1 ? rawPieces : [topic]);
}

export function buildIssueMap({
  researchTopic = '',
  decomposedIssues = [],
  kickoffReport = '',
  researchIntelligence = {},
}) {
  const intelligence = normalizeResearchIntelligence(researchIntelligence);
  const issues = inferIssueList(researchTopic, decomposedIssues);
  const reportText = normalizeLower(kickoffReport);
  const propositionMap = new Map(
    intelligence.propositionLedger.map((item, index) => [
      normalizeLower(item?.issue || item?.title || `prop_${index + 1}`),
      item,
    ]),
  );
  const mergedIssues = dedupeList([
    ...issues,
    ...intelligence.propositionLedger.map((item) => normalizeText(item?.issue || item?.title)).filter(Boolean),
  ]);

  return mergedIssues.map((issue, index) => {
    const tokens = tokenizeIssue(issue);
    const matchedTokens = tokens.filter((token) => reportText.includes(token));
    const proposition = propositionMap.get(normalizeLower(issue));
    const supportStatus = normalizeLower(proposition?.support_status || proposition?.supportStatus);
    const proofStatus = normalizeLower(proposition?.proof_status || proposition?.proofStatus);
    const isMapped = (
      supportStatus === 'supported'
      || supportStatus === 'partial'
      || tokens.length === 0
      || matchedTokens.length >= Math.min(2, tokens.length)
    );
    let coverageLabel = isMapped ? 'Mapped in current brief' : 'Still needs direct authority';
    if (supportStatus === 'supported') coverageLabel = 'Authority-backed';
    if (supportStatus === 'partial') coverageLabel = 'Needs tighter authority';
    if (supportStatus === 'unclear' || supportStatus === 'unsupported') coverageLabel = 'Still needs direct authority';
    if (proofStatus === 'record-supported' && supportStatus === 'supported') coverageLabel = 'Authority + record aligned';

    return {
      id: `issue_${index + 1}`,
      title: issue,
      status: isMapped ? 'mapped' : 'open',
      coverageLabel,
      matchedTokens,
      supportStatus,
      proofStatus,
      authorityTier: normalizeText(proposition?.authority_tier || proposition?.authorityTier),
      detail: normalizeText(proposition?.detail || proposition?.support_label || proposition?.supportLabel),
      nextStep: normalizeText(proposition?.next_step || proposition?.nextStep),
    };
  });
}

export function buildAuthorityMap({
  sources = [],
  activeSources = [],
  pinnedSources = [],
  primaryCourt = 'GENERAL',
}) {
  const combined = dedupeSourcesById([
    ...(pinnedSources || []),
    ...(activeSources || []),
    ...(sources || []),
  ]);

  const buckets = {
    binding: [],
    statutory: [],
    strong: [],
    persuasive: [],
    background: [],
  };
  const forumCounts = { SC: 0, HC: 0, TRIBUNAL: 0, GOV: 0, OTHER: 0 };

  combined.forEach((source) => {
    const tier = inferAuthorityTier(source, primaryCourt);
    const court = getAuthorityCourt(source);
    buckets[tier].push(source);
    forumCounts[court] += 1;
  });

  const orderedBuckets = Object.entries(AUTHORITY_BUCKET_META).map(([key, meta]) => ({
    key,
    label: meta.label,
    helper: meta.helper,
    count: buckets[key].length,
    sources: buckets[key],
  }));

  const tierRank = { binding: 0, statutory: 1, strong: 2, persuasive: 3, background: 4 };
  const pinnedIds = new Set((pinnedSources || []).map((source) => source.id));
  const activeIds = new Set((activeSources || []).map((source) => source.id));

  const topAuthorities = [...combined]
    .sort((left, right) => {
      const leftTier = inferAuthorityTier(left, primaryCourt);
      const rightTier = inferAuthorityTier(right, primaryCourt);
      const leftScore = (pinnedIds.has(left.id) ? -20 : 0) + (activeIds.has(left.id) ? -10 : 0) + tierRank[leftTier];
      const rightScore = (pinnedIds.has(right.id) ? -20 : 0) + (activeIds.has(right.id) ? -10 : 0) + tierRank[rightTier];
      return leftScore - rightScore;
    })
    .slice(0, 6)
    .map((source) => ({
      id: source.id,
      title: source.title,
      url: source.url,
      tier: inferAuthorityTier(source, primaryCourt),
      court: getAuthorityCourt(source),
      trustLabel: source.trust_label,
    }));

  return {
    totalSources: combined.length,
    activeCount: (activeSources || []).length,
    pinnedCount: (pinnedSources || []).length,
    buckets: orderedBuckets,
    forumCounts,
    topAuthorities,
  };
}

export function buildWeaknessSignals({
  sources = [],
  kickoffReport = '',
  workflowMode = 'MEMO',
  researchIntelligence = {},
}) {
  const intelligence = normalizeResearchIntelligence(researchIntelligence);
  const weaknessPatterns = [
    {
      id: 'conflict',
      pattern: /\b(conflict|contra|split|divergent|different view|another line)\b/i,
      title: 'Conflicting lines appear',
      detail: 'The issue may not sit on one clean line of authority.',
    },
    {
      id: 'facts',
      pattern: /\b(distinguish|distinguished|fact[- ]?sensitive|turns on facts|peculiar facts|facts differ)\b/i,
      title: 'Fact sensitivity risk',
      detail: 'The usable ratio may narrow if the bench treats this as fact-bound.',
    },
    {
      id: 'treatment',
      pattern: /\b(overruled|doubted|reconsider|referred|limited to)\b/i,
      title: 'Treatment check needed',
      detail: 'One or more authorities may need later-treatment verification.',
    },
    {
      id: 'uncertain',
      pattern: /\b(unclear|not settled|open question|subject to|depends on|prima facie)\b/i,
      title: 'Unsettled proposition',
      detail: 'The present material suggests the proposition may still be conditional.',
    },
  ];

  const matches = [];
  const pushIntelligenceSignal = (raw, group) => {
    if (!raw) return;
    const id = `${group}:${raw.id || raw.title || matches.length + 1}`;
    if (matches.some((item) => item.id === id)) return;
    matches.push({
      id,
      title: raw.title || (group === 'adverse' ? 'Counter-side risk' : 'Treatment check needed'),
      detail: raw.detail || raw.support_label || 'This point still needs another pass.',
      sourceTitle: raw.source_title || raw.sourceTitle || (group === 'adverse' ? 'Adverse authority' : 'Treatment signal'),
      severity: normalizeText(raw.severity),
    });
  };
  const pushMatch = (match) => {
    if (!matches.some((item) => item.id === match.id && item.sourceTitle === match.sourceTitle)) {
      matches.push(match);
    }
  };

  intelligence.adverseAuthorities.forEach((item) => pushIntelligenceSignal(item, 'adverse'));
  intelligence.treatmentSignals.forEach((item) => pushIntelligenceSignal(item, 'treatment'));

  dedupeSourcesById(sources).forEach((source) => {
    const sourceText = getSourceText(source);
    weaknessPatterns.forEach((pattern) => {
      if (pattern.pattern.test(sourceText)) {
        pushMatch({
          id: pattern.id,
          title: pattern.title,
          detail: source.reason || pattern.detail,
          sourceTitle: source.title,
        });
      }
    });
  });

  weaknessPatterns.forEach((pattern) => {
    if (pattern.pattern.test(kickoffReport || '')) {
      pushMatch({
        id: pattern.id,
        title: pattern.title,
        detail: pattern.detail,
        sourceTitle: 'Research brief',
      });
    }
  });

  if (matches.length > 0) return matches.slice(0, 5);

  if (workflowMode === 'HEARING' || workflowMode === 'BENCH') {
    return [{
      id: 'bench-pressure',
      title: 'Bench pressure still needs rehearsal',
      detail: 'Force-test the cleanest answer to maintainability, delay, jurisdiction, and relief.',
      sourceTitle: 'Workflow signal',
    }];
  }

  if (workflowMode === 'PETITION') {
    return [{
      id: 'ground-overreach',
      title: 'Grounds may be outrunning authority',
      detail: 'Each proposed ground should map to a specific authority or official source before drafting hard assertions.',
      sourceTitle: 'Workflow signal',
    }];
  }

  return [{
    id: 'counter-side',
    title: 'Opposition line not yet surfaced',
    detail: 'Actively identify the best authority and factual framing the other side will rely on first.',
    sourceTitle: 'Workflow signal',
  }];
}

export function buildMissingAuthoritySignals({
  authorityMap,
  primaryCourt = 'GENERAL',
  researchType = 'TOPIC',
  workflowMode = 'MEMO',
  contextDebug = [],
  researchIntelligence = {},
}) {
  const intelligence = normalizeResearchIntelligence(researchIntelligence);
  const missing = [];

  if ((authorityMap?.buckets || []).find((bucket) => bucket.key === 'binding')?.count === 0) {
    missing.push({
      id: 'binding',
      title: 'Binding authority is still thin',
      detail: primaryCourt === 'HC'
        ? 'Add a controlling Supreme Court or jurisdictional High Court authority before treating this as court-ready.'
        : 'Add at least one court-controlling authority before relying on the proposition.',
    });
  }

  if (primaryCourt === 'HC' && (authorityMap?.forumCounts?.HC || 0) === 0) {
    missing.push({
      id: 'hc',
      title: 'Jurisdictional High Court support missing',
      detail: 'A High Court matter usually needs a local HC line or a reason for departing from it.',
    });
  }

  if (researchType === 'STATUTE' && (authorityMap?.buckets || []).find((bucket) => bucket.key === 'statutory')?.count === 0) {
    missing.push({
      id: 'statutory',
      title: 'Bare Act / notification layer missing',
      detail: 'Pull in the statutory text, rules, circulars, or official notification before final analysis.',
    });
  }

  if ((workflowMode === 'HEARING' || workflowMode === 'BENCH') && (authorityMap?.topAuthorities || []).length < 3) {
    missing.push({
      id: 'hearing-depth',
      title: 'Need a tighter hearing stack',
      detail: 'Prepare a short stack of your three strongest authorities with one-line propositions and fallback distinctions.',
    });
  }

  if (intelligence.propositionLedger.some((item) => ['unsupported', 'unclear'].includes(normalizeLower(item.support_status || item.supportStatus)))) {
    missing.push({
      id: 'proposition-support',
      title: 'One or more propositions still lack a clean anchor',
      detail: 'At least one issue in the current ledger is still unsupported or too verification-heavy for hard reliance.',
    });
  }

  if ((contextDebug || []).length === 0) {
    missing.push({
      id: 'matter-context',
      title: 'Matter continuity has not surfaced yet',
      detail: 'Bring in reviewed matter files or Jolly memory if the argument depends on case posture, admissions, or chronology.',
    });
  }

  return missing.slice(0, 5);
}

export function buildProofGapSignals({
  issueMap = [],
  researchIntelligence = {},
  workflowMode = 'MEMO',
}) {
  const intelligence = normalizeResearchIntelligence(researchIntelligence);
  const gaps = intelligence.proofGaps.map((item, index) => ({
    id: item.id || `proof_${index + 1}`,
    title: item.title || 'Proof gap',
    detail: item.detail || 'The current line still needs record support.',
    sourceTitle: normalizeList(item.record_titles || item.recordTitles).join(', '),
    severity: item.severity || 'medium',
  }));

  if (gaps.length > 0) return gaps.slice(0, 4);

  const issueDrivenGaps = issueMap
    .filter((item) => normalizeLower(item.proofStatus) === 'record-gap')
    .map((item, index) => ({
      id: `issue_proof_${index + 1}`,
      title: `Record support still needed for ${item.title}`,
      detail: item.detail || 'The current issue needs a stronger factual bridge before hard use.',
      sourceTitle: item.nextStep || 'Issue map',
    }));

  if (issueDrivenGaps.length > 0) return issueDrivenGaps.slice(0, 4);

  if (workflowMode === 'HEARING' || workflowMode === 'PETITION' || workflowMode === 'BENCH') {
    return [{
      id: 'record-pressure',
      title: 'Pressure-test the record against the relief story',
      detail: 'Before courtroom use, make sure each core factual assertion has an annexure, admission, or chronology anchor.',
      sourceTitle: 'Workflow signal',
    }];
  }

  return [];
}

export function buildReliefPostureSignals({
  researchTopic = '',
  researchIntelligence = {},
  workflowMode = 'MEMO',
}) {
  const intelligence = normalizeResearchIntelligence(researchIntelligence);
  const posture = intelligence.reliefPosture.map((item, index) => ({
    id: item.id || `relief_${index + 1}`,
    title: item.title || 'Relief posture',
    detail: item.detail || 'Tighten the requested outcome against the current authority stack.',
    sourceTitle: item.source_title || item.sourceTitle || '',
  }));

  if (posture.length > 0) return posture.slice(0, 4);

  const topic = normalizeText(researchTopic) || 'this issue';
  if (workflowMode === 'HEARING' || workflowMode === 'BENCH') {
    return [{
      id: 'hearing-relief',
      title: 'Lead with the narrowest supportable ask',
      detail: `For ${topic}, keep the first oral ask tightly aligned to your cleanest authority and preserve a fallback direction if the bench resists the main prayer.`,
      sourceTitle: 'Workflow signal',
    }];
  }

  if (workflowMode === 'PETITION') {
    return [{
      id: 'petition-relief',
      title: 'Draft the broad prayer from the narrowest hard ground',
      detail: `For ${topic}, let the strongest current ground carry the main draft and flag any broader relief that still depends on inference or fresh authority.`,
      sourceTitle: 'Workflow signal',
    }];
  }

  return [{
    id: 'memo-relief',
    title: 'Convert the research into the safest usable position',
    detail: `For ${topic}, tie the recommendation to the strongest supported proposition and state the remaining uncertainty instead of leaping past it.`,
    sourceTitle: 'Workflow signal',
  }];
}

export function buildReadinessSummary({
  researchIntelligence = {},
  issueMap = [],
  proofGaps = [],
  weaknessSignals = [],
  missingAuthorities = [],
}) {
  const intelligence = normalizeResearchIntelligence(researchIntelligence);
  if (Object.keys(intelligence.readiness || {}).length > 0) {
    return {
      overallScore: Number(intelligence.readiness.overall_score || intelligence.readiness.overallScore || 0),
      postureLabel: normalizeText(intelligence.readiness.posture_label || intelligence.readiness.postureLabel || 'Building'),
      nextMove: normalizeText(intelligence.readiness.next_move || intelligence.readiness.nextMove),
      authorityCoverage: Number(intelligence.readiness.authority_coverage || intelligence.readiness.authorityCoverage || 0),
      proofCoverage: Number(intelligence.readiness.proof_coverage || intelligence.readiness.proofCoverage || 0),
    };
  }

  const mapped = issueMap.filter((item) => item.status === 'mapped').length;
  const overallScore = Math.max(10, Math.min(95, Math.round(
    (mapped * 20)
    + (Math.max(0, 4 - missingAuthorities.length) * 12)
    + (Math.max(0, 4 - proofGaps.length) * 10)
    + (Math.max(0, 4 - weaknessSignals.length) * 6)
  )));
  const postureLabel = overallScore >= 80
    ? 'Court-ready'
    : overallScore >= 60
      ? 'Promising but exposed'
      : overallScore >= 40
        ? 'Needs another pass'
        : 'Exploratory';

  return {
    overallScore,
    postureLabel,
    nextMove: 'Close the biggest authority or proof gap before taking a harder position.',
    authorityCoverage: mapped ? Math.round((mapped / Math.max(issueMap.length, 1)) * 100) : 0,
    proofCoverage: Math.max(0, 100 - (proofGaps.length * 20)),
  };
}

export function buildDeskQuickActions({
  workflowMode = 'MEMO',
  researchTopic = '',
}) {
  const topic = normalizeText(researchTopic) || 'this issue';
  const shared = [
    {
      id: 'authority-map',
      group: 'Frame',
      label: 'Authority map',
      prompt: `Build an authority map for ${topic}. Separate binding, strong support, persuasive, and official government material. Mark what I can safely rely on first.`,
    },
    {
      id: 'missing-authority',
      group: 'Frame',
      label: 'Missing authority',
      prompt: `Tell me what authority is still missing for ${topic}. Be explicit about what is binding, what is only persuasive, and what should come from a government source.`,
    },
    {
      id: 'weaknesses',
      group: 'Stress-test',
      label: 'Weakness finder',
      prompt: `Stress-test my current position on ${topic}. Show contradiction points, factual vulnerabilities, and the first attack the other side will make.`,
    },
    {
      id: 'bench-questions',
      group: 'Stress-test',
      label: 'Bench questions',
      prompt: `Prepare likely judge or bench questions for ${topic}, with the cleanest answer and a fallback answer for each.`,
    },
    {
      id: 'proposition-ledger',
      group: 'Frame',
      label: 'Proposition ledger',
      prompt: `Turn the current research on ${topic} into a proposition ledger. For each issue, mark whether it is supported, only partially supported, or still unclear, and show the exact authority and proof gap for each.`,
    },
    {
      id: 'later-treatment',
      group: 'Stress-test',
      label: 'Later treatment',
      prompt: `Check the current authority stack for ${topic} for later treatment risk. Tell me which authorities look followed, distinguished, doubted, or otherwise unsafe to lead with.`,
    },
    {
      id: 'adverse-authority',
      group: 'Stress-test',
      label: 'Adverse authority',
      prompt: `Show me the best adverse authority and opposition framing against ${topic}. Then give me the cleanest distinction or fallback answer.`,
    },
    {
      id: 'proof-gaps',
      group: 'Proof',
      label: 'Proof gaps',
      prompt: `List the factual and evidentiary gaps that still need to be closed for ${topic}. Be specific about what document, annexure, admission, chronology point, or client instruction is still missing.`,
    },
    {
      id: 'relief-posture',
      group: 'Relief',
      label: 'Relief posture',
      prompt: `Based on the current authorities and record, tell me the best primary relief, fallback ask, and safest courtroom framing for ${topic}.`,
    },
  ];

  const workflowSpecific = {
    HEARING: [
      {
        id: 'hearing-note',
        group: 'Draft',
        label: 'Hearing note',
        prompt: `Turn the current research on ${topic} into a sharp hearing note with oral sequence, authorities, fallback asks, and likely bench concerns.`,
      },
      {
        id: 'authorities-list',
        group: 'Draft',
        label: 'Authorities list',
        prompt: `Prepare a hearing-ready list of authorities for ${topic} with proposition, court weight, and a one-line oral use note for each.`,
      },
    ],
    PETITION: [
      {
        id: 'petition-grounds',
        group: 'Draft',
        label: 'Petition grounds',
        prompt: `Draft petition grounds for ${topic}. Mark every ground as either directly supported or still inferential.`,
      },
      {
        id: 'prayer-sheet',
        group: 'Draft',
        label: 'Prayer sheet',
        prompt: `Draft the primary prayer, interim prayer, and fallback prayer for ${topic}, making clear which asks are fully supported and which still need tighter authority or record support.`,
      },
    ],
    BRIEF: [
      {
        id: 'case-brief',
        group: 'Draft',
        label: 'Case brief',
        prompt: `Prepare a case brief for ${topic} covering facts, issues, holdings, ratio, and later treatment.`,
      },
    ],
    BENCH: [
      {
        id: 'bench-sheet',
        group: 'Draft',
        label: 'Bench sheet',
        prompt: `Prepare a bench question sheet for ${topic} with likely judicial questions, best answers, weak answers, and supporting authorities.`,
      },
      {
        id: 'oral-sequence',
        group: 'Draft',
        label: 'Oral sequence',
        prompt: `Build a tight oral sequence for ${topic}: opening frame, strongest authority, factual bridge, likely interruption, and fallback close.`,
      },
    ],
    IRAC: [
      {
        id: 'irac',
        group: 'Draft',
        label: 'IRAC answer',
        prompt: `Write an IRAC analysis for ${topic} using the current active authorities and note any gaps as uncertainties.`,
      },
    ],
    MEMO: [
      {
        id: 'desk-note',
        group: 'Draft',
        label: 'Desk note',
        prompt: `Draft a premium research desk note for ${topic} with issue, authority, risk, and immediate next use in live legal work.`,
      },
      {
        id: 'client-update',
        group: 'Draft',
        label: 'Client update',
        prompt: `Write a client-facing update on ${topic} that explains the current legal position, what is strong, what is still exposed, and what information we need next.`,
      },
    ],
  };

  return [...shared, ...(workflowSpecific[workflowMode] || workflowSpecific.MEMO)];
}

export function buildResearchDeskModel({
  researchTopic = '',
  workflowMode = 'MEMO',
  primaryCourt = 'GENERAL',
  researchType = 'TOPIC',
  sources = [],
  activeSources = [],
  pinnedSources = [],
  decomposedIssues = [],
  kickoffReport = '',
  contextDebug = [],
  researchIntelligence = {},
}) {
  const intelligence = normalizeResearchIntelligence(researchIntelligence);
  const authorityMap = buildAuthorityMap({
    sources,
    activeSources,
    pinnedSources,
    primaryCourt,
  });
  const issueMap = buildIssueMap({
    researchTopic,
    decomposedIssues,
    kickoffReport,
    researchIntelligence: intelligence,
  });
  const weaknessSignals = buildWeaknessSignals({
    sources: authorityMap.topAuthorities
      .map((authority) => dedupeSourcesById([...sources, ...activeSources, ...pinnedSources]).find((item) => item.id === authority.id))
      .filter(Boolean),
    kickoffReport,
    workflowMode,
    researchIntelligence: intelligence,
  });
  const missingAuthorities = buildMissingAuthoritySignals({
    authorityMap,
    primaryCourt,
    researchType,
    workflowMode,
    contextDebug,
    researchIntelligence: intelligence,
  });
  const proofGaps = buildProofGapSignals({
    issueMap,
    workflowMode,
    researchIntelligence: intelligence,
  });
  const reliefPosture = buildReliefPostureSignals({
    researchTopic,
    workflowMode,
    researchIntelligence: intelligence,
  });
  const readiness = buildReadinessSummary({
    researchIntelligence: intelligence,
    issueMap,
    proofGaps,
    weaknessSignals,
    missingAuthorities,
  });
  const workflow = WORKFLOW_MODE_DETAILS[workflowMode] || WORKFLOW_MODE_DETAILS.MEMO;

  return {
    issueMap,
    authorityMap,
    weaknessSignals,
    missingAuthorities,
    proofGaps,
    reliefPosture,
    treatmentSignals: intelligence.treatmentSignals,
    adverseAuthorities: intelligence.adverseAuthorities,
    readiness,
    jurisdictionMap: intelligence.jurisdictionMap,
    workflow,
    quickActions: buildDeskQuickActions({ workflowMode, researchTopic }),
  };
}

export function getWorkflowModeDetails(mode) {
  return WORKFLOW_MODE_DETAILS[mode] || WORKFLOW_MODE_DETAILS.MEMO;
}
